<script lang="ts" setup>
// Topbar v2 (chrome DS) — port de chrome/Topbar.jsx. Onglet du dossier courant à gauche ;
// cluster droit : pastille version, bascule thème, cloche notifications (LayoutV2NotifBell),
// menu compte. Données réelles : dossier (useOpportunityDetail), utilisateur (usersStore),
// version (runtimeConfig), notifications (store dédié).
// L'historique de versions de la maquette n'est pas porté : aucun endpoint ne l'expose.
import { onClickOutside, useClipboard } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useOpportunityDetail } from '~/composables/opportunities/useOpportunityDetail';

const config = useRuntimeConfig();
const route = useRoute();
const router = useRouter();
const usersStore = useUsersStore();
const { authUserData } = storeToRefs(usersStore);
const { opportunity } = useOpportunityDetail();

const version = computed<string>(() => String(config.public.APP_VERSION ?? ''));
const initials = computed<string>(() => (authUserData.value?.username ?? '').slice(0, 2).toUpperCase() || '—');
const userName = computed<string>(() => authUserData.value?.username ?? '');
const userRole = computed<string>(() => authUserData.value?.position?.slug ?? '');

// --- Onglet dossier courant (un seul onglet réel — le multi-dossiers de la maquette est mock).
const onDossier = computed<boolean>(() =>
  String(route.name ?? '').startsWith('opportunities-opportunityHashId') && Boolean(opportunity.value));
const reqId = computed<string | number>(() => opportunity.value?.reqId ?? '');
const dossierClient = computed<string>(() => {
  const persons = [...(opportunity.value?.persons ?? [])]
    .sort((a, b) => (b.main === true ? 1 : 0) - (a.main === true ? 1 : 0));
  const names = persons.map(p => p.lastName).filter(Boolean);
  return names.length > 1 ? `${names[0]} - ${names[1]}` : (names[0] ?? '');
});
// Libellé complet du dossier, tel qu'il est copié et annoncé par l'infobulle.
const dossierLabel = computed<string>(() => `#${reqId.value} ${dossierClient.value}`.trim());

// `legacy: true` : hors contexte sécurisé, `copy()` serait un no-op. Ce repli ne remonte pas ses
// échecs (VueUse ignore le retour d'`execCommand` et lève `copied` quand même), donc le toast plus
// bas ne couvre que l'API moderne. 1400 ms : durée de la confirmation en amont.
const { copy, copied } = useClipboard({ copiedDuring: 1400, legacy: true });

async function copyDossierLabel (): Promise<void> {
  try {
    await copy(dossierLabel.value);
  } catch (error) {
    console.error('[Topbar] copie du dossier', error);
    useToastService().add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Impossible de copier dans le presse-papier.',
      life: 3000,
    });
  }
}

// En `lg`, le kit masque `.gd-activity-name` et affiche `.gd-activity-name-short` : sans ce span,
// le nom du dossier disparaîtrait purement et simplement en fenêtre étroite. L'ellipse ne s'ajoute
// que si le nom est effectivement raccourci.
const dossierClientShort = computed<string>(() => {
  const full = dossierClient.value.trim();
  const first = full.split(/\s+/)[0];
  if (!first) { return ''; }
  return first === full ? first : `${first}…`;
});

const { isDark: dark, toggleColorMode: toggleTheme } = useAppearance();

// --- Overlay compte. Fermeture au clic extérieur + Échap.
const accountOpen = ref(false);
const accountRef = ref<HTMLElement | null>(null);
onClickOutside(accountRef, () => { accountOpen.value = false; });
function onKey (e: KeyboardEvent): void {
  if (e.key === 'Escape') { accountOpen.value = false; }
}
onMounted(() => { if (import.meta.client) { window.addEventListener('keydown', onKey); } });
onUnmounted(() => { if (import.meta.client) { window.removeEventListener('keydown', onKey); } });

function goNotifications (): void { accountOpen.value = false; router.push('/settings/notifications'); }
</script>

<template>
  <header class="gd-topbar">
    <!-- Pas de `role="tablist"`/`role="tab"` : l'onglet dossier n'est pas activable (le
         multi-dossiers de la maquette est un mock) et son texte doit rester sélectionnable. -->
    <div class="gd-activity-tabs">
      <div
        v-if="onDossier"
        class="gd-activity-tab active"
      >
        <span class="gd-activity-badge rac">RAC</span>
        <span class="gd-activity-id">#{{ reqId }}</span>
        <span class="gd-activity-name">{{ dossierClient }}</span>
        <span class="gd-activity-name-short">{{ dossierClientShort }}</span>
        <button
          type="button"
          :class="['gd-activity-copy', { 'is-copied': copied }]"
          :title="copied ? 'Copié' : `Copier « ${dossierLabel} »`"
          :aria-label="`Copier « ${dossierLabel} »`"
          @click="copyDossierLabel"
        >
          <!-- Aucune `color` : le masque suit le `currentColor` du bouton, survol et confirmation
               sont gérés en CSS (`.is-copied`). -->
          <DsIcon
            :name="copied ? 'check' : 'copy'"
            :size="12"
          />
        </button>
      </div>
    </div>

    <div class="gd-topbar-spacer" />

    <span class="gd-version-pill">v{{ version }}</span>

    <!-- Masquée par le kit sous `.gd-bp-lg` (la barre rend la place aux onglets) : le menu compte
         reprend la bascule, c'est le seul accès à ces largeurs.
         Exposée à tous dès la première MEP alors que seuls les écrans DS suivent le sombre : le
         chrome passe en sombre, le contenu legacy reste clair. Assumé, cf.
         docs/design-system-v2-mep.md §3 (et le test de useAppearance qui garde le `.dark` non posé). -->
    <button
      type="button"
      class="gd-topbar-icon gd-theme-toggle"
      :title="dark ? 'Passer en clair' : 'Passer en sombre'"
      aria-label="Basculer le thème"
      @click="toggleTheme"
    >
      <DsIcon
        :name="dark ? 'sun' : 'moon'"
        :size="18"
        color="var(--text-2)"
      />
    </button>

    <LayoutV2NotifBell />

    <span
      ref="accountRef"
      class="gd-account-wrap"
    >
      <button
        type="button"
        :class="['gd-account', { 'is-active': accountOpen }]"
        title="Mon compte"
        @click="accountOpen = !accountOpen"
      >
        <span class="gd-user-avatar">
          <span class="gd-user-initials">{{ initials }}</span>
          <span class="gd-user-dot" />
        </span>
        <span class="gd-account-name">{{ userName }}</span>
        <span
          class="gd-account-caret"
          aria-hidden="true"
        />
      </button>
      <div
        v-if="accountOpen"
        class="gd-account-pop"
        role="menu"
      >
        <div class="gd-account-pop-id">
          <span class="gd-account-pop-avatar">{{ initials }}</span>
          <span class="gd-account-pop-info">
            <span class="gd-account-pop-name">{{ userName }}</span>
            <span
              v-if="userRole"
              class="gd-account-pop-role"
            >{{ userRole }}</span>
          </span>
        </div>
        <div class="gd-account-pop-list">
          <button
            type="button"
            class="gd-account-pop-item"
            role="menuitem"
            @click="goNotifications"
          >
            <DsIcon
              name="bell"
              :size="15"
              color="var(--text-2)"
            />Notifications
          </button>
          <NuxtLink
            to="/settings/appearance"
            class="gd-account-pop-item"
            role="menuitem"
            @click="accountOpen = false"
          >
            <DsIcon
              name="palette"
              :size="15"
              color="var(--text-2)"
            />Apparence
          </NuxtLink>
          <!-- Relais de la bascule quand le kit masque l'icône de la topbar (`.gd-bp-lg`), et
               masqué sinon pour ne pas doublonner avec elle — cf. `.gd-theme-item` dans
               overrides.css. Rendue à toutes les largeurs : le popover reste ouvert au
               franchissement du point de rupture, il se repeint au thème obtenu. -->
          <button
            type="button"
            class="gd-account-pop-item gd-theme-item"
            role="menuitem"
            @click="toggleTheme"
          >
            <DsIcon
              :name="dark ? 'sun' : 'moon'"
              :size="15"
              color="var(--text-2)"
            />{{ dark ? 'Thème clair' : 'Thème sombre' }}
          </button>
        </div>
      </div>
    </span>
  </header>
</template>
